import { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import Chart from "react-apexcharts";
import { Button, Card, Popconfirm, Space, Tooltip, message } from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ColumnHeightOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FontSizeOutlined,
  PlusOutlined,
  SettingOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";

import SeriesSelectorRow from "./seriesSelectorRow";
import TableWidget from "./tableWidget";
import { buildChartOptions, getWidgetType } from "./widgetRegistry";
import {
  ROW_KINDS,
  applyChartCompare,
  getChartCompare,
  buildWidgetTable,
  getEffectiveFormat,
  getWidgetPeriods,
  isCustomTable,
  resolveSeries,
  supportsRowStyles,
  tableExportRows,
} from "./reportingUtils";
import { copyText, downloadWorkbook, exportFileName, rowsToTsv } from "../../utils/tableExport";

function ReportWidget({ widget, isFirst, isLast, ctx, handlers }) {
  const def = getWidgetType(widget.type);

  const fmt = useMemo(() => getEffectiveFormat(ctx.globalFmt, widget), [ctx.globalFmt, widget]);

  const periodCats = useMemo(
    () => getWidgetPeriods(ctx.periodCats, widget),
    [ctx.periodCats, widget]
  );

  const compare = useMemo(() => getChartCompare(widget, def), [widget, def]);

  // What's shown: for charts with a comparison, the Δ series too (or only).
  const series = useMemo(
    () =>
      applyChartCompare(
        widget.series.map((row) => resolveSeries(row, ctx, periodCats)).filter(Boolean),
        compare
      ),
    [widget.series, ctx, periodCats, compare]
  );

  const options = useMemo(
    () => (def.kind === "chart" ? buildChartOptions(widget, { periodCats, fmt, series, compare }) : null),
    [def.kind, widget, periodCats, fmt, series, compare]
  );

  // Remount the chart when type/settings change: ApexCharts doesn't reliably
  // pick up new formatter functions through a props update.
  // Showing/hiding the selection doesn't affect the chart, so it doesn't remount it.
  const { hideSelection, ...chartSettings } = widget.settings;
  const chartKey = `${widget.type}:${JSON.stringify(chartSettings)}`;

  const title = widget.settings.title || def.label;
  const notation = useSelector((state) => state.organization.organization?.notation);

  const table = useMemo(() => buildWidgetTable(widget, ctx, periodCats, series), [widget, ctx, periodCats, series]);
  const getRows = () => tableExportRows(table);
  const customTable = isCustomTable(widget);
  const rowStyles = supportsRowStyles(widget);
  // Custom-column tables can have data from sources none of the series use.
  const hasData = def.kind === "table" ? table.rows.length > 0 : series.length > 0;

  const copy = async () => {
    const ok = await copyText(rowsToTsv(getRows(), notation));
    if (ok) message.success("Data copied. Paste it into Excel or an email.");
    else message.error("The browser did not allow copying. Use Excel instead.");
  };

  return (
    <Card
      size="small"
      title={title}
      extra={
        <Space size={0}>
          <Tooltip title="Copy data">
            <Button type="text" size="small" icon={<CopyOutlined />} disabled={!hasData} onClick={copy} />
          </Tooltip>
          <Tooltip title="Open in Excel">
            <Button
              type="text"
              size="small"
              icon={<FileExcelOutlined />}
              disabled={!hasData}
              onClick={() => downloadWorkbook(exportFileName(title), [{ name: title, rows: getRows() }])}
            />
          </Tooltip>
          <Tooltip title="Move earlier">
            <Button
              type="text"
              size="small"
              icon={<ArrowLeftOutlined />}
              disabled={isFirst}
              onClick={() => handlers.moveWidget(widget, -1)}
            />
          </Tooltip>
          <Tooltip title="Move later">
            <Button
              type="text"
              size="small"
              icon={<ArrowRightOutlined />}
              disabled={isLast}
              onClick={() => handlers.moveWidget(widget, 1)}
            />
          </Tooltip>
          <Tooltip title={hideSelection ? "Show selection" : "Hide selection"}>
            <Button
              type="text"
              size="small"
              icon={hideSelection ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => handlers.toggleSelection(widget)}
            />
          </Tooltip>
          <Tooltip title="Settings">
            <Button
              type="text"
              size="small"
              icon={<SettingOutlined />}
              onClick={() => handlers.openSettings(widget)}
            />
          </Tooltip>
          <Popconfirm
            title="Remove this chart and its series?"
            okText="Remove"
            okButtonProps={{ danger: true }}
            onConfirm={() => handlers.deleteWidget(widget)}
          >
            <Tooltip title="Remove">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      }
    >
      {!hideSelection && (
        <>
          {widget.series.map((row, i) => (
            <SeriesSelectorRow
              key={row.series_id}
              row={row}
              isFirst={i === 0}
              isLast={i === widget.series.length - 1}
              ctx={ctx}
              handlers={handlers}
              hideSource={customTable}
              rowStyles={rowStyles}
            />
          ))}

          <Space size={8}>
            <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => handlers.addSeries(widget)}>
              Add series
            </Button>
            <Button
              type="dashed"
              size="small"
              icon={<UnorderedListOutlined />}
              onClick={() => handlers.openBulkAdd(widget)}
            >
              Add accounts
            </Button>
            {rowStyles && (
              <>
                <Button
                  type="dashed"
                  size="small"
                  icon={<FontSizeOutlined />}
                  onClick={() => handlers.addSpacerRow(widget, ROW_KINDS.heading)}
                >
                  Add heading
                </Button>
                <Button
                  type="dashed"
                  size="small"
                  icon={<ColumnHeightOutlined />}
                  onClick={() => handlers.addSpacerRow(widget, ROW_KINDS.blank)}
                >
                  Add blank row
                </Button>
              </>
            )}
          </Space>
        </>
      )}

      <div className="reporting-widget-body" style={{ marginTop: hideSelection ? 0 : 16 }}>
        {def.kind === "table" ? (
          <TableWidget table={table} fmt={fmt} />
        ) : (
          <Chart
            key={chartKey}
            type={def.apexType}
            options={options}
            series={series}
            height={340}
            width="100%"
          />
        )}
      </div>
    </Card>
  );
}

export default memo(ReportWidget);